import React, {useEffect} from "react";
import {css, Theme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {calculateRemSize, display, flex, flexDirection, mb, p, pb, ph, position, pt, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {PoiItemsList} from "../../../maps/components/PoiItemsList";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {QWActionButtons} from "../quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../quick_view/QWLocalizationInfo";
import {QWPictograms} from "../quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../quick_view/QWPropertyInfoHeading";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
}

export const MapOfferExpandablePanelQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedProperty = selectedIndex > properties.length - 1 ? properties[0] : properties[selectedIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    useEffect(() => {
        selectedProperty?.id && quickViewPropertyChange(offer.id, selectedProperty.id);
    }, [selectedProperty]);

    const handleDetailsButtonClick = () => {
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    };

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const propertyPlanImage = selectedProperty.plan_image_pages[0]?.image.p_img_760x428;
    const offerTypeHeaderTitle = getOfferTypeNamePluralWhat(offer.type);

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    if (isLoading) {
        return <Loader fullWidth size="lg" />;
    }
    return (
        <div css={wrapper}>
            <div css={headerStyle}>
                <QWHeaderWithPagination
                    handleQuickViewClose={handleQuickViewClose}
                    setSelectedIndex={setSelectedIndex}
                    propertyList={props.propertyList}
                    selectedIndex={selectedIndex}
                    onNextClickEvent={nextClickAnalyticsEvent}
                    onPrevClickEvent={prevClickAnalyticsEvent}
                    headerTitle={offerTypeHeaderTitle}
                />
            </div>
            <div css={contentStyle}>
                <QWImageWithButton
                    propertyLink={propertyLink}
                    alt={offer.name}
                    firstImageSrc={propertyPlanImage}
                    secondImageSrc={offer.main_image.m_img_760x428}
                    clickEvent={handleGalleryButtonClick}
                    css={imageStyle}
                    buttonCss={imageButtonStyle}
                    textInButtonsDisabled
                />

                <QWPropertyInfoHeading
                    rooms={selectedProperty.rooms}
                    offerType={offer.type}
                    offerName={offer.name}
                    highlightBoldText
                    textVariant="headline_4"
                    css={infoHeadingStyle}
                />

                <PoiItemsList offer={offer} css={[pb(3), pt(0.5)]} />
                <QWLocalizationInfo
                    address={offer.address}
                    distanceFromRegion={offer.stats?.distance_from_region}
                    offerType={offer.type}
                    css={[mb(5), flexDirection("column-reverse")]}
                    textVariant="info_txt_1"
                />

                <QWPictograms selectedProperty={selectedProperty} />

                <QWActionButtons
                    propertyLink={propertyLink}
                    vendorId={offer.vendor.id}
                    offerId={offer.id}
                    propertyId={selectedProperty.id}
                    propertyPrice={selectedProperty.price ?? null}
                    applicationSource={ApplicationSource.QuickViewPropertyModal}
                    applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                    clickEvent={handleDetailsButtonClick}
                    hideApplicationButton={isForSaleOverLimit}
                    css={buttonsStyle}
                    buttonSize="x-small"
                    buttonStyle={buttonStyle}
                />
            </div>
        </div>
    );
};

const wrapper = (theme: Theme) => css`
    ${display("flex")}
    ${flexDirection("column")};
    z-index: 1;
    background-color: ${theme.colors.gray[100]};
    ${p(0)};
`;

const buttonsStyle = css`
    ${flex("center", "center")};
    ${flexDirection("row")};
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: #fff;
    ${elevation(4)};
    ${p(2, 1)};
    gap: ${calculateRemSize(2)};
    ${w100};
`;

const buttonStyle = css`
    ${w100};
`;

const headerStyle = css`
    background-color: #fff;
    ${ph(1.5)};
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    max-height: calc(100vh - 60px - 54px);
    overflow-y: auto;
    overflow-x: hidden;
    ${p(0, 1.5, 7)};
    ${position("relative")};
`;

const imageStyle = css`
    width: 100vw;
    min-width: 100vw;
    margin-left: -${calculateRemSize(1.5)};
`;

const imageButtonStyle = css`
    min-width: ${calculateRemSize(4)};
    max-width: ${calculateRemSize(4)};
    min-height: ${calculateRemSize(4)};
    max-height: ${calculateRemSize(4)};
    ${p(0)};
`;

const infoHeadingStyle = css`
    ${ph(0)};
`;
import React, {useEffect} from "react";
import {css, Theme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {calculateRemSize, display, flex, flexDirection, mb, p, pb, ph, position, pt, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {PoiItemsList} from "../../../maps/components/PoiItemsList";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {QWActionButtons} from "../quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../quick_view/QWLocalizationInfo";
import {QWPictograms} from "../quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../quick_view/QWPropertyInfoHeading";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
}

export const MapOfferExpandablePanelQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedProperty = selectedIndex > properties.length - 1 ? properties[0] : properties[selectedIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    useEffect(() => {
        selectedProperty?.id && quickViewPropertyChange(offer.id, selectedProperty.id);
    }, [selectedProperty]);

    const handleDetailsButtonClick = () => {
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    };

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const propertyPlanImage = selectedProperty.plan_image_pages[0]?.image.p_img_760x428;
    const offerTypeHeaderTitle = getOfferTypeNamePluralWhat(offer.type);

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    if (isLoading) {
        return <Loader fullWidth size="lg" />;
    }
    return (
        <div css={wrapper}>
            <div css={headerStyle}>
                <QWHeaderWithPagination
                    handleQuickViewClose={handleQuickViewClose}
                    setSelectedIndex={setSelectedIndex}
                    propertyList={props.propertyList}
                    selectedIndex={selectedIndex}
                    onNextClickEvent={nextClickAnalyticsEvent}
                    onPrevClickEvent={prevClickAnalyticsEvent}
                    headerTitle={offerTypeHeaderTitle}
                />
            </div>
            <div css={contentStyle}>
                <QWImageWithButton
                    propertyLink={propertyLink}
                    alt={offer.name}
                    firstImageSrc={propertyPlanImage}
                    secondImageSrc={offer.main_image.m_img_760x428}
                    clickEvent={handleGalleryButtonClick}
                    css={imageStyle}
                    buttonCss={imageButtonStyle}
                    textInButtonsDisabled
                />

                <QWPropertyInfoHeading
                    rooms={selectedProperty.rooms}
                    offerType={offer.type}
                    offerName={offer.name}
                    highlightBoldText
                    textVariant="headline_4"
                    css={infoHeadingStyle}
                />

                <PoiItemsList offer={offer} css={[pb(3), pt(0.5)]} />
                <QWLocalizationInfo
                    address={offer.address}
                    distanceFromRegion={offer.stats?.distance_from_region}
                    offerType={offer.type}
                    css={[mb(5), flexDirection("column-reverse")]}
                    textVariant="info_txt_1"
                />

                <QWPictograms selectedProperty={selectedProperty} />

                <QWActionButtons
                    propertyLink={propertyLink}
                    vendorId={offer.vendor.id}
                    offerId={offer.id}
                    propertyId={selectedProperty.id}
                    propertyPrice={selectedProperty.price ?? null}
                    applicationSource={ApplicationSource.QuickViewPropertyModal}
                    applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                    clickEvent={handleDetailsButtonClick}
                    hideApplicationButton={isForSaleOverLimit}
                    css={buttonsStyle}
                    buttonSize="x-small"
                    buttonStyle={buttonStyle}
                />
            </div>
        </div>
    );
};

const wrapper = (theme: Theme) => css`
    ${display("flex")}
    ${flexDirection("column")};
    z-index: 1;
    background-color: ${theme.colors.gray[100]};
    ${p(0)};
`;

const buttonsStyle = css`
    ${flex("center", "center")};
    ${flexDirection("row")};
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: #fff;
    ${elevation(4)};
    ${p(2, 1)};
    gap: ${calculateRemSize(2)};
    ${w100};
`;

const buttonStyle = css`
    ${w100};
`;

const headerStyle = css`
    background-color: #fff;
    ${ph(1.5)};
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    max-height: calc(100vh - 60px - 54px);
    overflow-y: auto;
    overflow-x: hidden;
    ${p(0, 1.5, 7)};
    ${position("relative")};
`;

const imageStyle = css`
    width: 100vw;
    min-width: 100vw;
    margin-left: -${calculateRemSize(1.5)};
`;

const imageButtonStyle = css`
    min-width: ${calculateRemSize(4)};
    max-width: ${calculateRemSize(4)};
    min-height: ${calculateRemSize(4)};
    max-height: ${calculateRemSize(4)};
    ${p(0)};
`;

const infoHeadingStyle = css`
    ${ph(0)};
`;
